import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./UI";
import { BarChart3, BellRing } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const COLORS = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)", "var(--chart-5)", "var(--chart-6)", "var(--plum)", "var(--teal)"];
const usd = (n) => "$" + new Intl.NumberFormat("en-US", { notation: "compact" }).format(n);

export const SpendTrackerCard = () => {
  const [data, setData] = useState(null);

  const load = () => api.get("/political-os/opponent-spend").then(r => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const saveThreshold = async (val) => {
    const threshold = val === "" ? null : Number(val);
    if (threshold !== null && (isNaN(threshold) || threshold < 0)) return;
    try {
      await api.put("/political-os/spend-alert", { threshold });
      toast.success(threshold ? `Spend alerts on — pings the team at $${Number(threshold).toLocaleString()}+` : "Spend alerts off");
      load();
    } catch { toast.error("Could not save threshold"); }
  };

  if (data === false || data === null) return null;

  return (
    <Card padded={false} data-testid="spend-tracker-card">
      <div className="px-5 py-4 border-b border-white/10 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5 text-cyan-300" /> Opponent ad spend tracker
          </div>
          <div className="text-sm text-slate-300 mt-0.5">Dollar figures reported by ad trackers, FEC filings and press — collected with each daily snapshot.</div>
        </div>
        <div className="flex items-center gap-1.5" data-testid="spend-alert-setting" title="Ping the team when a single reported buy crosses this amount">
          <BellRing className="w-3.5 h-3.5 text-slate-500" />
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Alert at $</span>
          <input type="number" min="0" defaultValue={data.threshold || ""} placeholder="e.g. 500000"
            onBlur={(e) => Number(e.target.value || 0) !== Number(data.threshold || 0) && saveThreshold(e.target.value)}
            data-testid="spend-threshold-input"
            className="w-28 bg-slate-900/80 border border-white/15 rounded-md px-2 py-1 text-xs text-white placeholder-slate-600 focus:outline-none" />
        </div>
      </div>
      {data.series?.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-slate-500" data-testid="spend-tracker-empty">
          No reported spend figures found yet — they appear here as soon as ad buys are reported for this race.
        </div>
      ) : (
        <div className="px-3 py-4">
          <div className="h-56" data-testid="spend-tracker-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.series} margin={{ top: 6, right: 16, left: 4, bottom: 0 }}>
                <CartesianGrid stroke="rgb(var(--contrast-rgb) / 0.06)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "var(--text-muted)", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={usd} tick={{ fill: "var(--text-muted)", fontSize: 10 }} axisLine={false} tickLine={false} width={52} />
                <Tooltip contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--contrast-rgb) / 0.12)", borderRadius: 8, fontSize: 11 }}
                  formatter={(v) => usd(v)} labelStyle={{ color: "var(--text-muted)" }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {data.candidates.map((c, i) => (
                  <Line key={c} type="monotone" dataKey={c} stroke={COLORS[i % COLORS.length]}
                    strokeWidth={2} dot={{ r: 3 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="px-2 pt-2 space-y-1 max-h-28 overflow-y-auto">
            {(data.rows || []).slice(-6).reverse().map((r, i) => (
              <div key={r.id || i} className="flex items-center gap-2 text-[11px] text-slate-400" data-testid={`spend-row-${i}`}>
                <span className="font-mono text-slate-500">{r.date}</span>
                <span className="text-slate-200">{r.candidate}</span>
                <span className="font-mono text-cyan-300">{usd(r.amount_usd)}</span>
                <span className="truncate flex-1">{r.note}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};
